import { useEffect, useRef } from 'react';

/**
 * EqualizerBars — replaces the old LiquidWave. A sound-reactive-looking
 * frequency-analyser visual with stacked glowing bars. Fully themeable.
 *
 * Props:
 *   colors       string[] 2-4 gradient hex stops (top → bottom)
 *   barCount     number   how many bars
 *   barGap       number   px gap between bars
 *   speed        number   10-100 (how fast bars animate)
 *   glow         number   0-100 (glow intensity)
 *   height       number   container height px (default 280)
 *   peakColor    string   hex for peak caps
 */
export function EqualizerBars({
  colors = ['#FF3B30', '#DAA520', '#8B0000'],
  barCount = 48,
  barGap = 3,
  speed = 50,
  glow = 60,
  height = 280,
  peakColor = '#FFFFFF',
}) {
  const canvasRef = useRef(null);
  const visibleRef = useRef(false);
  const propsRef = useRef({});
  propsRef.current = { colors, barCount, barGap, speed, glow, peakColor };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();

    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    }, { threshold: 0.1 });
    observer.observe(canvas);

    // Simulated audio data — each bar has a phase + multi-frequency sum
    const bars = Array.from({ length: 200 }, (_, i) => ({
      phase: Math.random() * Math.PI * 2,
      freq: 0.5 + Math.random() * 2,
      peak: 0,
      peakFall: 0,
    }));

    let time = 0;
    const animate = () => {
      if (!visibleRef.current) { animId = requestAnimationFrame(animate); return; }
      const p = propsRef.current;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // speed 10-100 → dt .003 .03
      const dt = 0.003 + (p.speed / 100) * 0.027;
      time += dt;

      const n = Math.max(8, Math.min(160, p.barCount | 0));
      const gap = Math.max(0, p.barGap | 0);
      const barW = Math.max(1, (w - gap * (n - 1)) / n);

      // Build vertical gradient reused per bar
      const barGrad = ctx.createLinearGradient(0, h * 0.15, 0, h);
      const stops = p.colors && p.colors.length ? p.colors : ['#FF3B30', '#DAA520'];
      stops.forEach((c, i) => barGrad.addColorStop(i / Math.max(1, stops.length - 1), c));

      const glowAlpha = (p.glow || 0) / 100;

      for (let i = 0; i < n; i++) {
        const b = bars[i];
        // Multi-frequency amplitude: mimics EQ bars dancing to a beat
        const base = Math.sin(time * b.freq + b.phase) * 0.5 + 0.5;
        const mid = Math.sin(time * b.freq * 2 + i * 0.12) * 0.35;
        const hi = Math.sin(time * b.freq * 3.5 + i * 0.25) * 0.2;
        // Gentle sway across the width — low freq envelope
        const envelope = 0.55 + 0.45 * Math.sin(i / n * Math.PI + time * 0.4);
        let amp = Math.max(0.05, (base + mid * 0.5 + hi * 0.3) * envelope);
        amp = Math.min(1, amp);

        const barH = amp * h * 0.92;
        const x = i * (barW + gap);
        const y = h - barH;

        // Glow underlay
        if (glowAlpha > 0) {
          ctx.shadowBlur = 16 * glowAlpha;
          ctx.shadowColor = stops[0];
        } else {
          ctx.shadowBlur = 0;
        }

        ctx.fillStyle = barGrad;
        ctx.fillRect(x, y, barW, barH);

        // Peak hold
        if (amp > b.peak) { b.peak = amp; b.peakFall = 0; }
        else {
          b.peakFall += dt * 2;
          b.peak = Math.max(0, b.peak - dt * 0.6 - b.peakFall * dt * 0.8);
        }
        const peakY = h - b.peak * h * 0.92 - 4;
        ctx.shadowBlur = 0;
        ctx.fillStyle = p.peakColor || '#FFFFFF';
        ctx.globalAlpha = 0.9;
        ctx.fillRect(x, Math.max(0, peakY), barW, 2);
        ctx.globalAlpha = 1;
      }

      // Soft reflection at the bottom (optical polish)
      const refl = ctx.createLinearGradient(0, h * 0.7, 0, h);
      refl.addColorStop(0, 'rgba(0,0,0,0)');
      refl.addColorStop(1, 'rgba(0,0,0,0.55)');
      ctx.fillStyle = refl;
      ctx.fillRect(0, h * 0.7, w, h * 0.3);

      animId = requestAnimationFrame(animate);
    };
    animate();
    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);
    return () => { cancelAnimationFrame(animId); observer.disconnect(); window.removeEventListener('resize', handleResize); };
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#050505]" data-testid="equalizer-bars-section" style={{ height: `${height}px` }}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div className="absolute top-0 left-0 right-0 h-16 pointer-events-none" style={{ background: 'linear-gradient(to bottom, #0A0A0A, transparent)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none" style={{ background: 'linear-gradient(to top, #050505, transparent)' }} />
    </section>
  );
}
